import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './store/auth';
import { useApplyBranding } from './store/branding';
import { useWorkspace } from './store/workspace';
import { shouldOpenSoleCentre, soleCentre } from './components/nav';
import { api } from './lib/api';
import { Shell } from './components/Shell';
import { LoginPage } from './pages/LoginPage';
import { LegalPage } from './pages/LegalPage';
import { CentresPage } from './pages/CentresPage';
import { OrganizationsPage } from './pages/OrganizationsPage';
import { BrandingPage } from './pages/BrandingPage';
import { StudentsPage } from './pages/StudentsPage';
import { SchedulePage } from './pages/SchedulePage';
import { SessionPage } from './pages/SessionPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { TeachersPage } from './pages/TeachersPage';
import { ActivityPage } from './pages/ActivityPage';
import { TestsPage } from './pages/TestsPage';
import { TodayPage } from './pages/TodayPage';
import { TestReviewPage } from './pages/TestReviewPage';
import { WritePaperPage } from './pages/WritePaperPage';
import { StudentHomePage } from './pages/StudentHomePage';
import { ExamPage } from './pages/ExamPage';
import { ResultsPage } from './pages/ResultsPage';
import { ResultDetailPage } from './pages/ResultDetailPage';
import { ReviewPage } from './pages/ReviewPage';
import { StudentResultsPage } from './pages/StudentResultsPage';
import { AccountPage } from './pages/AccountPage';
import { InstructionsPage } from './pages/InstructionsPage';
import type { UserRole } from './lib/types';

/**
 * Every question screen on one page, for comparing against the real exam.
 *
 * Loaded on demand and routed only in development, so its fixtures — a page of
 * sample passages and questions — are never part of what a candidate
 * downloads before an exam.
 */
const ScreensPage = lazy(() =>
  import('./pages/ScreensPage').then((m) => ({ default: m.ScreensPage })),
);

/** Where each role belongs when they land on the root. */
const HOME: Record<UserRole, string> = {
  SUPER_ADMIN: '/admin/organizations',
  INSTRUCTOR: '/today',
  STUDENT: '/my/tests',
};

function RequireRole({ roles, children }: { roles: UserRole[]; children: React.ReactNode }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  // Sending a signed-in user to their own home rather than an error page: a
  // stale bookmark for the wrong role is a navigation mistake, not a failure.
  if (!roles.includes(user.role)) return <Navigate to={HOME[user.role]} replace />;
  return <>{children}</>;
}

export function App() {
  const { user, organization, ready, restore } = useAuth();
  const restoreWorkspace = useWorkspace((w) => w.restore);
  const organizationId = useWorkspace((w) => w.organizationId);
  const chooseCentre = useWorkspace((w) => w.choose);
  const leftDeliberately = useWorkspace((w) => w.leftDeliberately);

  useEffect(() => {
    void restore();
    // Brings back the centre an administrator was working inside, so a reload
    // mid-task does not drop them back to the list.
    restoreWorkspace();
  }, [restore, restoreWorkspace]);

  /**
   * Opens the only centre there is.
   *
   * The choice is remembered for the tab, not the machine — deliberately, so a
   * shared computer does not resume somebody else's centre — which means a new
   * tab starts with none open and the teaching screens locked. On a platform
   * running one centre that is a question with one answer, and being asked it
   * has three times looked like the library, the students and the results had
   * been deleted.
   *
   * Not after they have left one on purpose, though — see shouldOpenSoleCentre.
   */
  useEffect(() => {
    if (
      !shouldOpenSoleCentre({
        ready,
        role: user?.role,
        centreOpen: Boolean(organizationId),
        leftDeliberately,
      })
    ) {
      return undefined;
    }
    let cancelled = false;
    api
      .get<{ organizations: { id: string; name: string }[] }>('/organizations')
      .then(({ data }) => {
        const only = soleCentre(data.organizations);
        if (only && !cancelled) chooseCentre(only);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [ready, user?.role, organizationId, leftDeliberately, chooseCentre]);

  useApplyBranding(organization);

  if (!ready) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <p className="eyebrow">Loading</p>
      </div>
    );
  }

  return (
    <Routes>
      {/*
        * Every exam screen on one page, without sitting a paper to reach it.
        *
        * Open in development, and on a live deployment to the platform
        * administrator — who is the person asked "does this look right?" and
        * until now had to assign a paper to themselves to answer. It is a
        * gallery of the platform's own screens with no centre's data in it,
        * so the only thing gating it protects is a page nobody else needs.
        */}
      <Route
        path="/dev/screens"
        element={
          import.meta.env.DEV ? (
            <Suspense fallback={null}>
              <ScreensPage />
            </Suspense>
          ) : (
            <RequireRole roles={['SUPER_ADMIN']}>
              <Suspense fallback={null}>
                <ScreensPage />
              </Suspense>
            </RequireRole>
          )
        }
      />

      {/* Readable without signing in: the people most likely to need it are a
          candidate deciding what a score here means and a centre owner
          deciding whether to use it. */}
      <Route path="/legal" element={<LegalPage />} />
      {/* For centres: what Target is and what it costs. Its own address, so
          the site's front door stays the sign-in a candidate needs. */}
      <Route path="/centres" element={<CentresPage />} />
      {/* The front door: what Target is, for anyone not signed in — sign-in
          is one click away, and each centre's own link (/o/…) still opens
          its sign-in directly. Someone already signed in goes to their home. */}
      <Route path="/" element={user ? <Navigate to={HOME[user.role]} replace /> : <CentresPage />} />
      <Route path="/login" element={user ? <Navigate to={HOME[user.role]} replace /> : <LoginPage />} />
      <Route path="/o/:slug" element={user ? <Navigate to={HOME[user.role]} replace /> : <LoginPage />} />

      {/* The exam screen replaces the whole window — no console chrome, as in
          the real test — so it is routed outside the Shell. */}
      <Route
        path="/my/exam/:assignmentId"
        element={
          <RequireRole roles={['STUDENT']}>
            <ExamPage />
          </RequireRole>
        }
      />

      <Route
        element={
          <RequireRole roles={['SUPER_ADMIN', 'INSTRUCTOR', 'STUDENT']}>
            <Shell />
          </RequireRole>
        }
      >
        <Route
          path="/account"
          element={
            <RequireRole roles={['SUPER_ADMIN', 'INSTRUCTOR', 'STUDENT']}>
              <AccountPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/instructions"
          element={
            <RequireRole roles={['SUPER_ADMIN']}>
              <InstructionsPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/organizations"
          element={
            <RequireRole roles={['SUPER_ADMIN']}>
              <OrganizationsPage />
            </RequireRole>
          }
        />
        <Route
          path="/today"
          element={
            <RequireRole roles={['INSTRUCTOR', 'SUPER_ADMIN']}>
              <TodayPage />
            </RequireRole>
          }
        />
        <Route
          path="/tests"
          element={
            <RequireRole roles={['INSTRUCTOR', 'SUPER_ADMIN']}>
              <TestsPage />
            </RequireRole>
          }
        />
        <Route
          path="/tests/new"
          element={
            <RequireRole roles={['INSTRUCTOR', 'SUPER_ADMIN']}>
              <WritePaperPage />
            </RequireRole>
          }
        />
        <Route
          path="/tests/:id"
          element={
            <RequireRole roles={['INSTRUCTOR', 'SUPER_ADMIN']}>
              <TestReviewPage />
            </RequireRole>
          }
        />
        <Route
          path="/students"
          element={
            <RequireRole roles={['INSTRUCTOR', 'SUPER_ADMIN']}>
              <StudentsPage />
            </RequireRole>
          }
        />
        <Route
          path="/schedule"
          element={
            <RequireRole roles={['INSTRUCTOR', 'SUPER_ADMIN']}>
              <SchedulePage />
            </RequireRole>
          }
        />
        <Route
          path="/schedule/:id"
          element={
            <RequireRole roles={['INSTRUCTOR', 'SUPER_ADMIN']}>
              <SessionPage />
            </RequireRole>
          }
        />
        <Route
          path="/analytics"
          element={
            <RequireRole roles={['INSTRUCTOR', 'SUPER_ADMIN']}>
              <AnalyticsPage />
            </RequireRole>
          }
        />
        <Route
          path="/teachers"
          element={
            <RequireRole roles={['SUPER_ADMIN']}>
              <TeachersPage />
            </RequireRole>
          }
        />
        <Route
          path="/activity"
          element={
            <RequireRole roles={['SUPER_ADMIN']}>
              <ActivityPage />
            </RequireRole>
          }
        />
        <Route
          path="/settings/branding"
          element={
            <RequireRole roles={['SUPER_ADMIN']}>
              <BrandingPage />
            </RequireRole>
          }
        />
        <Route
          path="/my/tests"
          element={
            <RequireRole roles={['STUDENT']}>
              <StudentHomePage />
            </RequireRole>
          }
        />
        <Route
          path="/my/results"
          element={
            <RequireRole roles={['STUDENT']}>
              <StudentResultsPage />
            </RequireRole>
          }
        />
        <Route
          path="/my/results/:id"
          element={
            <RequireRole roles={['STUDENT']}>
              <ResultDetailPage />
            </RequireRole>
          }
        />
        <Route
          path="/my/results/:id/review"
          element={
            <RequireRole roles={['STUDENT']}>
              <ReviewPage />
            </RequireRole>
          }
        />
        <Route
          path="/results"
          element={
            <RequireRole roles={['INSTRUCTOR', 'SUPER_ADMIN']}>
              <ResultsPage />
            </RequireRole>
          }
        />
        <Route
          path="/results/:id"
          element={
            <RequireRole roles={['INSTRUCTOR', 'SUPER_ADMIN']}>
              <ResultDetailPage />
            </RequireRole>
          }
        />
        <Route
          path="/results/:id/review"
          element={
            <RequireRole roles={['INSTRUCTOR', 'SUPER_ADMIN']}>
              <ReviewPage />
            </RequireRole>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to={user ? HOME[user.role] : '/login'} replace />} />
    </Routes>
  );
}
